import type { Country } from '@/components/BrandProfile/CountrySelector'
import { RegistrationProgress } from '@/components/ProgressBar/RegistrationProgress'
import api from '@/services/api'
import { notificationService } from '@/services/notificationService'
import { useRegionsQuery } from '@/services/staticValues/queries'
import useAccessStore from '@/store/accessStore'
import { ErrorHandler } from '@/utils/ErrorHandler'
import { API_ENDPOINTS } from '@/utils/apiEndpoints'
import { type ChangeEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useBrandUserStore from '../../store/brandUserStore'
import RegistrationYourProfileForm from './RegistrationYourProfileForm'
import styles from './registration.module.css'

export const RegistrationYourProfile = () => {
  const navigate = useNavigate()
  const { setValue } = useBrandUserStore()
  const { data: regions } = useRegionsQuery()
  const { brandData } = useAccessStore()

  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '+40',
    region: '',
    selectedCountry: '40', // Add country code tracking
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (brandData) {
      setValues((prev) => ({
        ...prev,
        firstName: brandData.firstName || '',
        lastName: brandData.lastName || '',
        phoneNumber: brandData.phoneNumber || '+40',
        region: brandData.region || '',
      }))
    }
  }, [brandData])

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target
    const sanitized = value.replace(/[^a-zA-ZăĂâÂîÎțȚșȘ -]/g, '')
    setValues((prev) => ({ ...prev, [id]: sanitized }))
    setValue(id, sanitized)

    if ((errors as Record<string, string>)[id]) {
      setErrors((prev) => ({ ...prev, [id]: '' }))
    }
  }

  const handlePhoneChange = (value: string) => {
    let sanitized = value.replace(/[^\d+]/g, '')

    if (sanitized.includes('+')) {
      sanitized = `+${sanitized.replace(/\+/g, '').replace(/^\+{2,}/, '')}`
    }

    setValues((prev) => ({ ...prev, phoneNumber: sanitized }))
    setValue('phoneNumber', sanitized)

    if (errors.phoneNumber) {
      setErrors((prev) => ({ ...prev, phoneNumber: '' }))
    }
  }

  const handleCountryChange = (country: Country) => {
    setValues((prev) => ({ ...prev, selectedCountry: country.code }))
    setValue('selectedCountry', country.code)
  }

  const handleRegionChange = (value: string) => {
    setValues((prev) => ({ ...prev, region: value }))
    setValue('region', value)
    if (errors.region) {
      setErrors((prev) => ({ ...prev, region: '' }))
    }
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!values.firstName.trim()) newErrors.firstName = 'Name is required'
    if (!values.lastName.trim()) newErrors.lastName = 'Surname is required'

    // Updated phone validation to handle international format with dial code
    const phoneRegex = /^\+?[0-9\s]{7,17}$/
    if (!values.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required'
    } else if (!phoneRegex.test(values.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number'
    }

    if (!values.region) newErrors.region = 'Please select a region'
    setErrors(newErrors)

    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0]
      notificationService.error(firstError)
    }

    return Object.keys(newErrors).length === 0
  }

  const handleNext = async () => {
    if (!validate()) return
    try {
      await api.post(API_ENDPOINTS.BRAND.REGISTER_STEP_TWO, {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        region: values.region,
      })
      await useAccessStore.getState().fetchBrandData()
      navigate('/registration/industries')
    } catch (error) {
      console.error('Failed to submit profile step:', error)
      const axiosError = error as { response?: { data?: unknown } }
      const parsed = ErrorHandler.parseApiErrorResponse(axiosError.response?.data)
      if (parsed && Object.keys(parsed).length > 0) {
        const keyMap: Record<string, string> = { phone: 'phoneNumber', phone_number: 'phoneNumber' }
        const mapped: Record<string, string> = {}
        for (const [k, v] of Object.entries(parsed)) {
          const targetKey = keyMap[k] || k
          mapped[targetKey] = v as string
        }
        setErrors((prev) => ({ ...prev, ...mapped }))

        const firstError = Object.values(mapped)[0]
        if (firstError) {
          notificationService.error(firstError)
        }
      }
    }
  }

  const regionOptions = regions?.items.map((region: string) => ({
    value: region,
    label: region,
  }))

  return (
    <>
      <div className={styles.mainContentWrapper}>
        <h1 className={styles.title}>Your Profile</h1>
        <p className={styles.textGrey}>Enter your personal details to create your account.</p>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <RegistrationProgress />
        </div>

        <RegistrationYourProfileForm
          values={values}
          // errors={errors}
          onChange={handleChange}
          onPhoneChange={handlePhoneChange}
          onCountryChange={handleCountryChange}
          onRegionChange={handleRegionChange}
          onNext={handleNext}
          onBack={async () => {
            try {
              const { default: useAccessStore } = await import('@/store/accessStore')
              await useAccessStore.getState().logout()
            } finally {
              navigate('/login', { replace: true })
            }
          }}
          regionOptions={regionOptions || []}
          nextDisabled={
            !values.firstName.trim() ||
            !values.lastName.trim() ||
            !values.phoneNumber.trim() ||
            !values.region ||
            Object.values(errors).some((e) => !!e)
          }
        />
      </div>
    </>
  )
}
